import type {
  ControlNode as ControlNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import { VariablePortAddMenu } from "./VariablePortMenus.js";
import {
  TerminalPortContextMenu,
  type TerminalPortContextMenuState,
} from "./TerminalPortContextMenu.js";
import dico from "../../../../util/src/language/dico.js";
import InputIcon from "@mui/icons-material/Input";
import AddIcon from "@mui/icons-material/Add";
import { IconButton } from "@mui/material";
import React, { useCallback, useState } from "react";

export interface VariableBeginNodeProps {
  node: ControlNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onAddInput?: (valueType: string, label: string) => void;
  onRenameInput?: (portId: string, label: string) => void;
  onRemoveInput?: (portId: string) => void;
  readOnly?: boolean;
}

/**
 * Entry terminal for scene variable graphs.
 * Starts with no ports; extra value outputs are added via the bottom-right control.
 */
const VariableBeginNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onAddInput,
  onRenameInput,
  onRemoveInput,
  readOnly = false,
}: VariableBeginNodeProps) => {
  const behaviorDico = dico().behavior;
  const graphLabels = behaviorDico.sceneCatalog.variableGraph;
  const outputPorts = node.ports.filter((port) => port.direction === "output");

  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [portMenu, setPortMenu] = useState<TerminalPortContextMenuState | null>(
    null,
  );

  const handleAddClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAddAnchor(event.currentTarget);
    },
    [],
  );

  const handlePortContextMenu = useCallback(
    (event: React.MouseEvent, portId: string) => {
      if (readOnly) return;
      event.preventDefault();
      event.stopPropagation();
      const port = node.ports.find((entry) => entry.id === portId);
      setPortMenu({
        anchor: event.currentTarget as HTMLElement,
        portId,
        label: port?.label ?? "",
      });
    },
    [node.ports, readOnly],
  );

  return (
    <div
      style={{
        minWidth: 200,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background:
            "linear-gradient(180deg, #c4b5fd 0%, #7c3aed 55%, #5b21b6 100%)",
        }}
      >
        <InputIcon sx={{ fontSize: 14 }} />
        {behaviorDico.controls?.variableBegin ?? ""}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          padding: "6px 8px 4px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {outputPorts.map((port) => (
          <div
            key={port.id}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              minHeight: 28,
            }}
            onContextMenu={(event) => handlePortContextMenu(event, port.id)}
          >
            <span
              style={{
                fontSize: 11,
                color: "#374151",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {port.label}
            </span>
            <PortHandle
              node={node}
              port={port}
              setPortsMap={setPortsMap}
              onMouseDown={() => onPortMouseDown?.(node.id, port)}
              onMouseUp={() => onPortMouseUp?.(node.id, port)}
              isConnected={isPortConnected?.(node.id, port.id) ?? false}
              onContextMenu={(event) => handlePortContextMenu(event, port.id)}
            />
          </div>
        ))}
        {!readOnly && onAddInput ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 4,
              paddingBottom: 6,
            }}
          >
            <IconButton
              size="small"
              aria-label={graphLabels.addInputPort}
              onClick={handleAddClick}
              sx={{ p: 0.25 }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </div>
        ) : (
          <div style={{ paddingBottom: 6 }} />
        )}
      </div>
      {!readOnly && onAddInput ? (
        <VariablePortAddMenu
          anchorEl={addAnchor}
          open={Boolean(addAnchor)}
          onClose={() => setAddAnchor(null)}
          onConfirm={onAddInput}
        />
      ) : null}
      {!readOnly && onRenameInput && onRemoveInput ? (
        <TerminalPortContextMenu
          portMenu={portMenu}
          labels={graphLabels}
          onClose={() => setPortMenu(null)}
          onRename={onRenameInput}
          onRemove={onRemoveInput}
        />
      ) : null}
    </div>
  );
};

export const VariableBeginNode = React.memo(VariableBeginNodeComponent);
